import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Building2, LogIn, Eye, EyeOff } from 'lucide-react';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KlantLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(`${API_URL}/api/klant-portal/auth/login`, {
        email,
        password
      });

      const { access_token, user, klant_naam } = response.data;
      
      // Validate token before storing
      if (!access_token) {
        toast.error('Geen geldige token ontvangen');
        return;
      }
      
      // Clear any existing klant session data first
      localStorage.removeItem('klant_token');
      localStorage.removeItem('klant_user');
      localStorage.removeItem('klant_naam');
      
      // BELANGRIJK: Clear ook medewerker sessie om conflict te voorkomen
      // Een gebruiker kan niet tegelijk medewerker EN klant zijn in dezelfde browser sessie
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Store new token and user data
      localStorage.setItem('klant_token', access_token);
      localStorage.setItem('klant_user', JSON.stringify(user));
      localStorage.setItem('klant_naam', klant_naam);
      
      // Verify storage was successful
      const storedToken = localStorage.getItem('klant_token');
      if (!storedToken) {
        toast.error('Kon inloggegevens niet opslaan. Controleer uw browserinstellingen.');
        return;
      }

      toast.success(`Welkom ${user.naam}!`);
      navigate('/klant/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="text-center mb-8">
          <img 
            src="/wasz-logo.png" 
            alt="WASZ.nl Logo" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">Klantenportaal</h1>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <Label htmlFor="email">E-mailadres</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="uw@email.nl"
              required
              data-testid="klant-email-input"
            />
          </div>

          <div>
            <Label htmlFor="password">Wachtwoord</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="klant-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="klant-login-btn"
          >
            {loading ? (
              'Inloggen...'
            ) : (
              <>
                <LogIn className="w-4 h-4 mr-2" />
                Inloggen
              </>
            )}
          </Button>
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-sm text-cyan-600 hover:text-cyan-800 hover:underline"
              data-testid="klant-forgot-password-link"
            >
              Wachtwoord vergeten?
            </button>
          </div>
        </form>

        <ForgotPasswordDialog open={showForgot} onClose={() => setShowForgot(false)} mode="klant" />

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center mb-3">
            Nog geen toegang of problemen met inloggen?
          </p>
          <a
            href="mailto:services@waszbase.co.nl?subject=Klantenportaal%20toegang"
            className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm"
            data-testid="contact-email-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="16" x="2" y="4" rx="2"/>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
            </svg>
            Neem contact op met WASZ
          </a>
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            Bent u medewerker?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:underline"
            >
              Klik hier voor medewerker login
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default KlantLogin;
